import React, { useState, useEffect } from "react";
import Layout2 from "@/components/Layout/Layout2";
import { onlineOrderingLinks } from "@/components/lib/SidebarLinks";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { toast } from "react-hot-toast";
import {
  fetchOrderDetails,
  acceptOrder,
  cancelOrder,
} from "@/redux/slice/order-ordering-slice/order-status-slice";
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useSelector, useDispatch } from "react-redux";
import { Separator } from "@/components/ui/separator";
import { useParams } from "react-router-dom";
import OnlyTableSekeleton from "@/components/skeleton/only-table-skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Info,
  ListChecks,
  MessageSquareText,
  CalendarDays,
  Wallet,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { formatLabel } from "@/lib/utils";
import { formatCurrency } from "@/lib/utils";
import { Printer, CheckCircle2, XCircle } from "lucide-react";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import { printCustomerCopy } from "@/lib/print-slip";
import PrintKitchenCopyModal from "@/components/TakeOder/PrintKitchenCopyModal";
import hasPermission from "@/hooks/hasPermission";

const SingleOrderRequest = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    action: null, // 'accept' or 'cancel'
  });
  const [showKitchenPrintModal, setShowKitchenPrintModal] = useState(false);
  const { orderDetails, getLoading, updateLoading } = useSelector(
    (state) => state.onlineOrdering.orderStatus,
  );
  const fallbackOrder = {
    order_summary: {
      order_id: "N/A",
      order_time_date: "N/A",
      order_type: "N/A",
      order_status: "N/A",
      pre_order: {
        is_pre_order: false,
        date_time: "N/A",
      },
    },
    customer_info: {
      customer_name: "N/A",
      phone_number: "N/A",
      email: "N/A",
      delivery_address: "N/A",
    },
    items: [],
    payment_info: {
      sub_total: 0,
      discount: 0,
      delivery_charge: 0,
      total: 0,
      payment_status: "N/A",
      payment_method: "N/A",
    },
    pre_order: {
      advance_amount: 0,
      payment_method: "Cash on Delivery",
      status: "N/A",
      pre_order_date: "N/A",
    },
  };

  const order = orderDetails || fallbackOrder;
  const orderSummary = order?.order_summary || fallbackOrder.order_summary;
  const customerInfo = order?.customer_info || fallbackOrder.customer_info;
  const paymentInfo = order?.payment_info || fallbackOrder.payment_info;
  const items = order?.items || [];

  useEffect(() => {
    if (id) {
      dispatch(fetchOrderDetails(id));
    }
  }, [dispatch, id]);

  const handleConfirmAction = async () => {
    if (confirmDialog.action === "accept") {
      const result = await dispatch(acceptOrder(id));
      if (result.meta.requestStatus === "fulfilled") {
        toast.success("Order accepted successfully");
      } else {
        toast.error(result.payload || "Failed to accept order");
      }
    } else if (confirmDialog.action === "cancel") {
      const result = await dispatch(cancelOrder(id));
      if (result.meta.requestStatus === "fulfilled") {
        toast.success("Order canceled successfully");
      } else {
        toast.error(result.payload || "Failed to cancel order");
      }
    }
    setConfirmDialog({ isOpen: false, action: null });
  };

  const openConfirmDialog = (action) => {
    setConfirmDialog({ isOpen: true, action });
  };

  const getItemExtrasSummary = (item) => {
    const categories = [
      "extra_addons",
      "extra_salads",
      "extra_sauces",
      "extra_toppings",
      "banquet_extra_items",
    ];

    let totalCount = 0;
    categories.forEach((key) => {
      if (Array.isArray(item?.[key])) {
        totalCount += item[key].filter((sub) => sub?.canceled !== true).length;
      }
    });

    if (totalCount === 0) return null;

    return (
      <Badge
        variant="outline"
        className="text-[10px] font-normal border-cyellow/30 text-cyellow bg-cyellow/5"
      >
        {totalCount} Extra{totalCount > 1 ? "s" : ""}
      </Badge>
    );
  };

  const ItemDetailsModal = ({ item }) => {
    const categoriesMapping = {
      addons: { label: "Add-ons", color: "text-sky-500", showPrice: false },
      extra_addons: {
        label: "Extra Add-ons",
        color: "text-indigo-500",
        showPrice: true,
      },
      salads: { label: "Salads", color: "text-emerald-500", showPrice: false },
      extra_salads: {
        label: "Extra Salads",
        color: "text-teal-500",
        showPrice: true,
      },
      sauces: { label: "Sauces", color: "text-orange-500", showPrice: false },
      extra_sauces: {
        label: "Extra Sauces",
        color: "text-amber-500",
        showPrice: true,
      },
      toppings: { label: "Toppings", color: "text-red-500", showPrice: false },
      extra_toppings: {
        label: "Extra Toppings",
        color: "text-rose-500",
        showPrice: true,
      },
      modifiers: {
        label: "Modifiers",
        color: "text-purple-500",
        showPrice: false,
      },
      banquet_selected_items: {
        label: "Banquet Items",
        color: "text-blue-500",
        showPrice: false,
      },
      banquet_extra_items: {
        label: "Banquet Extras",
        color: "text-violet-500",
        showPrice: true,
      },
    };

    const groupedExtras = Object.entries(categoriesMapping)
      .map(([key, config]) => ({
        ...config,
        items: Array.isArray(item?.[key])
          ? item[key].filter((i) => i?.canceled !== true)
          : [],
      }))
      .filter((group) => group.items.length > 0);

    return (
      <Dialog>
        <DialogTrigger asChild>
          <button className="text-[11px] font-medium text-cyellow hover:text-cyellow/80 flex items-center gap-1.5 transition-all group">
            <div className="bg-cyellow/10 p-1 rounded group-hover:bg-cyellow/20 transition-colors">
              <ListChecks className="w-3 h-3" />
            </div>
            Display All Selections
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px] border-[#333] bg-[#121212] text-color5 shadow-2xl">
          <DialogHeader className="border-b border-[#333] pb-5">
            <div className="flex items-center gap-3">
              <div className="bg-cyellow/20 p-2.5 rounded-xl border border-cyellow/20">
                <Info className="w-5 h-5 text-cyellow" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold tracking-tight">
                  {item?.name || "Item Details"}
                </DialogTitle>
                {item?.variant && (
                  <p className="text-xs text-cyellow font-medium mt-0.5 uppercase tracking-wide">
                    {typeof item.variant === "object"
                      ? item.variant?.name
                      : item.variant}
                  </p>
                )}
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] mt-2 pr-4">
            <div className="space-y-5 py-4">
              {groupedExtras.length > 0 ? (
                groupedExtras.map((group, idx) => (
                  <div key={idx} className="space-y-3">
                    <h4
                      className={`text-[11px] font-bold uppercase tracking-[0.1em] flex items-center gap-2 ${group.color} opacity-90`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${group.color.replace("text-", "bg-")}`}
                      />
                      {group.label}
                    </h4>
                    <div className="grid grid-cols-1 gap-2 pl-4">
                      {group.items.map((sub, sIdx) => (
                        <div
                          key={sIdx}
                          className="flex items-center justify-between text-sm bg-white/[0.02] p-3 rounded-lg border border-white/10 hover:bg-white/[0.06] transition-colors"
                        >
                          <span className="font-medium text-gray-100 flex items-center gap-2">
                            {sub?.preFix && (
                              <span className="text-cyellow/70 text-[9px] border border-cyellow/30 px-1.5 py-0.5 rounded uppercase">
                                {sub.preFix}
                              </span>
                            )}
                            {sub?.name || "Unnamed"}
                          </span>
                          {group.showPrice && sub?.price && (
                            <span className="text-cyellow font-bold text-xs bg-cyellow/10 px-2 py-1 rounded border border-cyellow/20">
                              +{formatCurrency(sub.price)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 bg-white/[0.02] rounded-2xl border border-dashed border-white/10">
                  <p className="text-sm text-gray-500 italic">
                    No additional selections.
                  </p>
                </div>
              )}

              {item?.notes && (
                <div className="mt-4 pt-5 border-t border-[#333]/50">
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-cyellow mb-3 flex items-center gap-2">
                    <MessageSquareText className="w-3.5 h-3.5" />
                    Kitchen Notes
                  </h4>
                  <div className="bg-cyellow/5 border border-cyellow/10 px-4 py-3 rounded-xl text-sm italic text-gray-300 leading-relaxed shadow-inner">
                    "{item.notes}"
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="border-t border-[#333] pt-5 mt-2 flex justify-end">
            <div className="text-xs font-semibold px-4 py-2 bg-white/5 rounded-full border border-white/10 text-gray-300">
              Quantity:{" "}
              <span className="text-cyellow font-bold text-sm ml-1">
                {item?.quantity || 0}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={onlineOrderingLinks} />}
      pageName={{
        icon: true,
        title: `Order ID : ${orderSummary?.online_order_unique_id || "N/A"}`,
      }}
      paths={[
        { name: "Home", path: "/home" },
        // { name: "Online Ordering", path: "/online-ordering" },
        {
          name: "Website Ordering",
          path: "/onlineordering/website-ordering/order-request",
        },
        {
          name: "Order Request",
          path: "/onlineordering/website-ordering/order-request",
        },
        { name: "Single Order" },
      ]}
    >
      {getLoading && <OnlyTableSekeleton header={false} />}
      {!getLoading && (
        <div>
          <div
            className={`container grid grid-cols-1 gap-6 py-5 ${orderSummary.pre_order?.is_pre_order
              ? "lg:grid-cols-3 md:grid-cols-2"
              : "lg:grid-cols-2 md:grid-cols-2"
              }`}
          >
            {/* Order Summary */}
            <div className="flex flex-col">
              <h2 className="py-2 text-lg font-semibold text-color5 flex items-center gap-2">
                Order Summary
              </h2>
              <Card className="flex-1 border p-5 text-sm flex flex-col gap-3 shadow-md bg-cblack2">
                <p className="flex justify-between border-b border-white/5 pb-2">
                  <div className="w-[120px] flex items-center justify-between gap-2">
                    <div className="text-gray-400">Order ID</div>
                    <div>:</div>
                  </div>
                  <span className="font-medium">
                    #{orderSummary?.online_order_unique_id || "N/A"}
                  </span>
                </p>
                <p className="flex justify-between border-b border-white/5 pb-2">
                  <div className="w-[120px] flex items-center justify-between gap-2">
                    <div className="text-gray-400">Time & Date</div>
                    <div>:</div>
                  </div>
                  <span className="font-medium">
                    {orderSummary.order_time_date || "N/A"}
                  </span>
                </p>
                <p className="flex justify-between border-b border-white/5 pb-2">
                  <div className="w-[120px] flex items-center justify-between gap-2">
                    <div className="text-gray-400">Order Type</div>
                    <div>:</div>
                  </div>
                  <span className="font-medium">
                    {orderSummary.order_type || "N/A"}
                  </span>
                </p>
                <p className="flex justify-between">
                  <div className="w-[120px] flex items-center justify-between gap-2">
                    <div className="text-gray-400">Order Status</div>
                    <div>:</div>
                  </div>
                  <span className="text-cyellow font-bold">
                    {orderSummary.order_status || "N/A"}
                  </span>
                </p>
                <p className="flex justify-between">
                  <div className="w-[120px] flex items-center justify-between gap-2">
                    <div className="text-gray-400">Order Source</div>
                    <div>:</div>
                  </div>
                  <span className="text-cyellow font-bold">
                    {order?.source == "website" ? "Website" : order?.source == "smart_qr_code" ? "Smart QR Code" : "QR Code"}
                  </span>
                </p>
                {order?.applied_online_discount_offer && (
                  <p className="flex justify-between border-t border-white/5 pt-2 mt-1">
                    <div className="w-[120px] flex items-center justify-between gap-2">
                      <div className="text-gray-400">Applied Offer</div>
                      <div>:</div>
                    </div>
                    <span className="text-color11 font-bold flex items-center gap-1.5">
                      🎁 {order.applied_online_discount_offer.name}
                    </span>
                  </p>
                )}
              </Card>
            </div>

            {/* Customer Info */}
            <div className="flex flex-col">
              <h2 className="py-2 text-lg font-semibold text-color5">
                Customer Info
              </h2>
              <Card className="flex-1 border p-5 text-sm flex flex-col gap-3 shadow-md bg-cblack2">
                <p className="flex justify-between border-b border-white/5 pb-2">
                  <div className="w-[120px] flex items-center justify-between gap-2">
                    <div className="text-gray-400">Name</div>
                    <div>:</div>
                  </div>
                  <span className="font-medium text-right line-clamp-1">
                    {customerInfo.customer_name || "N/A"}
                  </span>
                </p>
                <p className="flex justify-between border-b border-white/5 pb-2">
                  <div className="w-[120px] flex items-center justify-between gap-2">
                    <div className="text-gray-400">Phone</div>
                    <div>:</div>
                  </div>
                  <span className="font-medium">
                    {customerInfo.phone_number || "N/A"}
                  </span>
                </p>
                <p className="flex justify-between border-b border-white/5 pb-2">
                  <div className="w-[120px] flex items-center justify-between gap-2">
                    <div className="text-gray-400">Email</div>
                    <div>:</div>
                  </div>
                  <span className="font-medium text-right truncate pl-4">
                    {customerInfo.email || "N/A"}
                  </span>
                </p>
                <p className="flex justify-between border-b border-white/5 pb-2">
                  <div className="w-[120px] flex items-center justify-between gap-2">
                    <div className="text-gray-400">Delivery Address</div>
                    <div>:</div>
                  </div>
                  <span className="font-medium text-xs text-gray-300 bg-white/5 p-2 rounded">
                    {customerInfo.delivery_address || "N/A"}
                  </span>
                </p>
              </Card>
            </div>

            {/* Pre-Order Details */}
            {orderSummary?.pre_order?.is_pre_order && (
              <div className="flex flex-col ">
                <h2 className="py-2 text-color5 text-lg font-semibold flex items-center gap-2 tracking-tight">
                  Pre-Order Details
                </h2>
                <Card className="flex-1 p-5 text-sm flex flex-col gap-4 bg-cblack2 overflow-hidden relative group shadow-lg border">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-color11/5 rounded-full -mr-16 -mt-16 blur-3xl group-hover:bg-color11/10 transition-colors" />

                  <div className="space-y-1 relative">
                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.2em]">
                      Planned For arrival
                    </p>
                    <p className="text-sm font-bold text-color5 bg-color11/10 p-2 rounded-lg border border-color11/20 flex items-center justify-center text-center">
                      {order?.pre_order?.pre_order_date &&
                        order.pre_order.pre_order_date !== "N/A"
                        ? format(
                          parseISO(order.pre_order.pre_order_date),
                          "EEE, MMM do, yyyy - hh:mm a",
                        )
                        : orderSummary.pre_order.date_time || "N/A"}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 relative pt-1">
                    <div className="space-y-1 bg-white/5 p-2 rounded-lg border border-white/5">
                      <p className="text-[10px] text-gray-500 font-bold uppercase">
                        Advance
                      </p>
                      <p className="text-base font-black ">
                        {formatCurrency(order?.pre_order?.advance_amount || 0)}
                      </p>
                    </div>
                    <div className="space-y-1 bg-white/5 p-2 rounded-lg border border-white/5 flex flex-col items-center justify-center">
                      <p className="text-[10px] text-gray-500 font-bold uppercase">
                        Status
                      </p>
                      <Badge
                        variant="outline"
                        className="bg-color11/10 border-color11/30 font-black text-[9px] py-0 px-1.5 uppercase leading-tight"
                      >
                        {order?.pre_order?.status || "Pending"}
                      </Badge>
                    </div>
                  </div>

                  {order?.pre_order?.payment_method && (
                    <div className="flex items-center gap-2 pt-3 border-t border-white/10 mt-auto">
                      <Wallet className="w-3.5 h-3.5 text-color11 opacity-70" />
                      <span className="text-[11px] text-gray-400 font-medium italic">
                        Guaranteed:{" "}
                        {order.pre_order.payment_method.replace(/_/g, " ")}
                      </span>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
          <div className="container grid grid-cols-1 py-5 pt-0">
            <div>
              <h2 className=" py-2 text-lg font-semibold text-color5 flex items-center gap-2">
                <ListChecks className="w-5 h-5 text-color11" />
                Order Items Details
              </h2>
              <Card className="bg-[#121212]/50 col-span-1 md:col-span-2 overflow-hidden border-[#333]">
                <Table>
                  <TableHeader className="bg-white/5">
                    <TableRow className="border-b-[#333] hover:bg-transparent">
                      <TableHead className="text-color5 font-medium px-6 text-[13px] uppercase tracking-wider">
                        Product Information
                      </TableHead>
                      <TableHead className="text-color5 font-medium  px-6 text-center text-[13px] uppercase tracking-wider w-[120px]">
                        Qty
                      </TableHead>
                      <TableHead className="text-color5 font-medium  px-6 text-right text-[13px] uppercase tracking-wider w-[140px]">
                        Price
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items && items.length > 0 ? (
                      items
                        ?.filter((item) => item?.canceled !== true)
                        ?.map((item, i) => (
                          <TableRow
                            key={item?.order_item_id || i}
                            className="border-b border-[#333]/50 hover:bg-white/[0.04] transition-all group"
                          >
                            <TableCell className="py-2 px-6">
                              <div className="flex flex-col gap-2">
                                {/* Product Name */}
                                <span className="font-medium text-color5 text-base group-hover:text-color11 transition-colors">
                                  {item?.name || "Unnamed Product"}
                                </span>

                                {/* Variant Badge */}
                                {item?.variant && (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] bg-cyellow/10 text-cyellow px-2.5 rounded-full  uppercase tracking-tighter border border-cyellow/20">
                                      {typeof item.variant === "object"
                                        ? item.variant?.name
                                        : item.variant}
                                    </span>
                                    {item?.variant?.calorie_amount && (
                                      <span className="text-[9px] bg-white/10 text-gray-300 px-2  rounded-full">
                                        {item.variant.calorie_amount} cal
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Extras Summary and Modal */}
                                <div className="flex flex-wrap items-center gap-2 pt-2">
                                  {getItemExtrasSummary(item)}
                                  <ItemDetailsModal item={item} />
                                </div>

                                {/* Kitchen Notes */}
                                {item?.notes && (
                                  <div className="mt-2 text-[10px] text-cyellow/80 flex items-start gap-2 bg-cyellow/5 p-2 rounded-lg border border-cyellow/10 italic">
                                    <MessageSquareText className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    <span>{item.notes}</span>
                                  </div>
                                )}
                              </div>
                            </TableCell>

                            {/* Quantity */}
                            <TableCell className="text-center align-middle px-6">
                              <span className="bg-cyellow/10 text-cyellow text-sm px-3 py-1 rounded-lg border border-cyellow/20">
                                {item?.quantity || 0} PCS
                              </span>
                            </TableCell>

                            {/* Total Price */}
                            <TableCell className="text-right align-middle px-6">
                              <span className="font-bold text-base text-cyellow">
                                {formatCurrency(item?.calculated_price || 0)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan="3"
                          className="text-center py-24 text-gray-500"
                        >
                          <div className="flex flex-col items-center gap-3 opacity-30">
                            <ListChecks className="w-12 h-12" />
                            <p className="text-xl font-medium italic">
                              No items found for this order.
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>
            </div>
          </div>
          <div className="container flex justify-between align-middle py-5 pt-0">
            <div className="w-80">
              <h2 className=" py-2 text-lg font-semibold text-color5">
                Payment Info
              </h2>
              <Card className="col-span-1 border p-5 text-sm">
                <p className="pb-3">
                  <span className="mr-5">Payment Status</span>:
                  <span className="text-cyellow">
                    {" "}
                    {formatLabel(paymentInfo.payment_status || "N/A")}
                  </span>
                </p>
                <p>
                  <span className="mr-[7px]">Payment Method</span>
                  <span>
                    : {formatLabel(paymentInfo.payment_method || "N/A")}
                  </span>
                </p>
              </Card>
            </div>
            <div className="w-80">
              <Card className="col-span-1 border p-5 text-sm">
                <p className="flex justify-between pb-3">
                  <span className="mr-[26px]">Sub Total</span>
                  <span>
                    {formatCurrency(
                      Number(paymentInfo.sub_total || 0) +
                        Number(paymentInfo.discount || 0) +
                        Number(paymentInfo.online_discount || 0)
                    )}
                  </span>
                </p>
                <p className="flex justify-between pb-3">
                  <span className="mr-[34px]">Discount</span>
                  <span>
                    -{formatCurrency(
                      Number(paymentInfo.discount || 0) +
                        Number(paymentInfo.online_discount || 0)
                    )}
                  </span>
                </p>
                {order?.applied_online_discount_offer && (
                  <p className="flex justify-between pb-3 pl-3 text-color11 text-xs font-medium">
                    <span>- {order.applied_online_discount_offer.name}</span>
                    <span>
                      -{formatCurrency(
                        order.applied_online_discount_offer.discount_amount || 0
                      )}
                    </span>
                  </p>
                )}
                <p className="flex justify-between pb-5">
                  <span className="mr-[104px]">Delivery Charge</span>
                  <span>{formatCurrency(paymentInfo.delivery_charge)}</span>
                </p>
                <Separator className="top-8" />
                <p className="flex justify-between text-cyellow pt-3">
                  <span className="mr-6">Total</span>
                  <span className="font-medium text-base">
                    {formatCurrency(paymentInfo.total)}
                  </span>
                </p>
              </Card>
            </div>
          </div>
          <div className="container flex gap-4 py-5 pt-0 flex-wrap">
            {/* Action Buttons */}
            <div className="flex gap-3 items-center flex-wrap">
              {/* Accept/Accepted Button */}
              {order?.status === "accepted" ? (
                <Button
                  disabled
                  className="bg-green-600/20 border border-green-600/40 text-green-400 rounded-lg py-3 px-6 font-semibold flex items-center gap-2 cursor-not-allowed"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Accepted
                </Button>
              ) : order?.status === "cancelled" ? null : (
                <Button
                  isLoading={updateLoading}
                  disabled={updateLoading || !hasPermission("online_ordering.website_order.accept_order")}
                  onClick={() => openConfirmDialog("accept")}
                  className={`bg-color11 hover:bg-color11/90 rounded-lg py-3 px-6 text-color5 font-semibold transition-all ${!hasPermission("online_ordering.website_order.accept_order") ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  Accept Order
                </Button>
              )}

              {/* Print Buttons - Show only when accepted */}
              {order?.status === "accepted" && (
                <>
                  <Button onClick={() => printCustomerCopy(order?.order_id)} className="bg-cviolet hover:bg-cviolet/90 rounded-lg py-3 px-6 text-color5 font-semibold flex items-center gap-2 transition-all">
                    <Printer className="w-4 h-4" />
                    Print Customer Copy
                  </Button>
                  <Button onClick={() => setShowKitchenPrintModal(true)} className="bg-color12 hover:bg-color12/90 rounded-lg py-3 px-6 text-color5 font-semibold flex items-center gap-2 transition-all">
                    <Printer className="w-4 h-4" />
                    Print Kitchen Copy
                  </Button>
                </>
              )}

              {/* Cancel/Cancelled Button */}
              {order?.status === "cancelled" ? (
                <Button
                  disabled
                  className="bg-red-600/20 border border-red-600/40 text-red-400 rounded-lg py-3 px-6 font-semibold flex items-center gap-2 cursor-not-allowed"
                >
                  <XCircle className="w-4 h-4" />
                  Cancelled
                </Button>
              ) : order?.status !== "accepted" ? (
                <Button
                  disabled={updateLoading || !hasPermission("online_ordering.website_order.cancel_order")}
                  onClick={() => openConfirmDialog("cancel")}
                  className={`bg-color14 hover:bg-color14/90 rounded-lg py-3 px-6 text-color5 font-semibold transition-all ${!hasPermission("online_ordering.website_order.cancel_order") ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  Cancel Order
                </Button>
              ) : null}
            </div>
          </div>
          {/* Confirm Dialog for Accept/Cancel */}
          <DeleteConfirmDialog
            isOpen={confirmDialog.isOpen}
            onClose={() => setConfirmDialog({ isOpen: false, action: null })}
            onConfirm={handleConfirmAction}
            icon={
              confirmDialog.action === "accept" ? (
                <CheckCircle2 className="text-green-500 w-6 h-6" />
              ) : (
                <XCircle className="text-red-500 w-6 h-6" />
              )
            }
            title={
              confirmDialog.action === "accept"
                ? "Accept Order"
                : "Cancel Order"
            }
            description={
              confirmDialog.action === "accept"
                ? "Are you sure you want to accept this order? This will confirm the order and proceed with processing."
                : "Are you sure you want to cancel this order? This action cannot be undone."
            }
            yesBtnTxt={
              confirmDialog.action === "accept" ? "Yes, Accept" : "Yes, Cancel"
            }
            noBtnTxt="No, Go Back"
            yesBtnClass={
              confirmDialog.action === "accept"
                ? "bg-green-600 hover:bg-green-700"
                : "bg-red-600 hover:bg-red-700"
            }
            isLoading={updateLoading}
          />

          <PrintKitchenCopyModal
            isOpen={showKitchenPrintModal}
            onClose={() => setShowKitchenPrintModal(false)}
            isTabletView={false}
            order={order}
          />
        </div>
      )}
    </Layout2>
  );
};

export default SingleOrderRequest;
